import { useDebounce } from 'ahooks';
import { GetProp, Select, SelectProps } from 'antd';
import { ReactElement, ReactNode, useState } from 'react';
import { ChevronDown, Search } from 'react-bootstrap-icons';

import { FormSelectNotFoundContent } from '@/common/components';
import { ArrayElement, QueryError } from '@/common/interfaces';
import { buildQuery } from '@/common/services';

type OmitFirst<T extends any[]> = T extends [infer _, ...infer Rest] ? Rest : never;

type Query<D extends Data> = (
  params: { query: string; includedIds?: string[]; excludedIds?: string[] },
  ...args: any[]
) => ReturnType<typeof buildQuery<NonNullable<D>, QueryError>>;
type Data = Record<string, any>[] | undefined;

type Props<Q extends Query<D>, D extends Data = ReturnType<ReturnType<Q>>['data']> = Omit<
  SelectProps,
  'children' | 'options' | 'optionRender'
> & {
  notFoundDescription: string;
  query: Q;
  searchWithoutQuery?: boolean;
  excludedIds?: string[];
  optionItemRender?: (data: ArrayElement<NonNullable<D>>) => ReactNode;
  onSelectValue?: (data: ArrayElement<NonNullable<D>>) => void;
} & (
    | { options?: never; children: (data: NonNullable<D>) => Iterable<ReactElement> | ReactElement }
    | { options: (data: D | undefined) => SelectProps['options']; children?: never }
  ) &
  ({ queryArgs?: never } | { queryArgs: NonNullable<OmitFirst<Parameters<Q>>> });

export const AppAutocomplete: <Q extends Query<D>, D extends Data = ReturnType<ReturnType<Q>>['data']>(
  props: Props<Q, D>,
) => ReactElement<Props<Q, D>> & ReturnType<typeof Select> = ({
  notFoundDescription,
  query,
  queryArgs,
  children,
  options,
  onSelectValue,
  searchWithoutQuery = false,
  excludedIds,
  ...otherProps
}) => {
  const {
    loading,
    value,
    disabled,
    open,
    mode,
    autoClearSearchValue = mode !== 'multiple',
    onSearch,
    onClear,
    onOpenChange,
    onSelect,
    optionItemRender,
    onChange,
    ...op
  } = otherProps;

  const [stateData, setStateData] = useState<ReturnType<ReturnType<typeof query>>['data']>();
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [dropdownIsOpen, setDropdownIsOpen] = useState(open);

  const debouncedSearchQuery = useDebounce(searchQuery, { wait: 400 });

  const queryArg = (() => {
    if (searchWithoutQuery) return '';

    return searchQuery && debouncedSearchQuery ? debouncedSearchQuery : '';
  })();

  const includedIdsArg = (() => {
    if (searchWithoutQuery) {
      return undefined;
    }

    if (mode === 'multiple' || mode === 'tags') {
      return [value || []].flat();
    }

    return searchQuery && debouncedSearchQuery ? undefined : [value || []].flat();
  })();

  const { isLoading, error, isError, refetch, remove } = query(
    { query: queryArg, includedIds: includedIdsArg, excludedIds },
    Array.isArray(queryArgs) ? queryArgs.flat() : [].flat(),
  )({
    onSuccess: setStateData,
  });

  const handleChangeSearchQuery: GetProp<SelectProps, 'onSearch'> = (searchVal) => {
    if (!searchWithoutQuery) {
      setSearchQuery(searchVal);
    }

    onSearch?.(searchVal);
  };

  const handleClear: GetProp<SelectProps, 'onClear'> = () => {
    setSearchQuery(undefined);

    remove();

    onClear?.();
  };

  const handleSelect: GetProp<SelectProps, 'onSelect'> = (selectedVal, opts) => {
    if (!searchWithoutQuery && autoClearSearchValue) {
      setSearchQuery(undefined);
      remove();
    }

    onSelect?.(selectedVal, opts);
  };

  const handleChange: GetProp<SelectProps, 'onChange'> = (value, option) => {
    onChange?.(value, option);

    const el = stateData?.find((el) => el.id === value) as ArrayElement<NonNullable<typeof stateData>>;
    onSelectValue?.(el);
  };

  const handleDropdownVisibleChange: GetProp<SelectProps, 'onOpenChange'> = (visibilityState) => {
    setDropdownIsOpen(visibilityState);

    refetch();

    onOpenChange?.(visibilityState);
  };

  const handleOptionRender: NonNullable<SelectProps['optionRender']> = ({ data }) => {
    const el = data.item as ArrayElement<NonNullable<typeof stateData>> | undefined;
    return el ? optionItemRender?.(el) : null;
  };

  return (
    <Select
      {...op}
      open={dropdownIsOpen}
      mode={mode}
      autoClearSearchValue={autoClearSearchValue}
      showSearch
      allowClear
      notFoundContent={
        (Array.isArray(stateData) || isError) && (
          <FormSelectNotFoundContent description={notFoundDescription} isError={isError} error={error} />
        )
      }
      options={options?.(stateData)}
      disabled={disabled}
      loading={isLoading || loading}
      value={isLoading && !stateData ? undefined : value}
      onChange={handleChange}
      onSearch={handleChangeSearchQuery}
      onClear={handleClear}
      onSelect={handleSelect}
      onOpenChange={handleDropdownVisibleChange}
      defaultActiveFirstOption={false}
      filterOption={
        searchWithoutQuery
          ? (inputValue, option) => {
              return String(option?.label).toLowerCase().includes(String(inputValue.toLowerCase()));
            }
          : false
      }
      suffixIcon={dropdownIsOpen ? <Search /> : <ChevronDown />}
      optionRender={Array.isArray(stateData) && optionItemRender ? handleOptionRender : undefined}
    >
      {stateData ? (
        children?.(stateData)
      ) : (
        <FormSelectNotFoundContent description={notFoundDescription} isError={isError} error={error} />
      )}
    </Select>
  );
};
